/**
 * Tenant Dashboard Tab
 * Overview for tenant admins with tenant-specific statistics
 */

import { useTenantStats } from "@hooks/useTenantAdmin";
import { cn } from "@utils/cn";

// Stat card component
interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  loading?: boolean;
}

function StatCard({ title, value, subtitle, icon, loading }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </p>
          {loading ? (
            <div className="mt-2 h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              {value}
            </p>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
        </div>
        <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <div className="w-6 h-6 text-primary-600 dark:text-primary-400">
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function UsersIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function TemplateIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function WorkshopIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}

// Role breakdown card
interface RoleBreakdownProps {
  byRole: Record<string, number>;
  loading?: boolean;
}

function RoleBreakdown({ byRole, loading }: RoleBreakdownProps) {
  const roleLabels: Record<string, string> = {
    participant: "Participants",
    instructor: "Instructors",
    tenant_admin: "Admins",
    super_admin: "Super Admins",
  };

  const roleOrder = [
    "participant",
    "instructor",
    "tenant_admin",
    "super_admin",
  ];
  const sortedRoles = roleOrder.filter((role) => byRole[role] !== undefined);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
        Users by Role
      </h3>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {sortedRoles.map((role) => (
            <div key={role} className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {roleLabels[role] || role}
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {byRole[role]}
              </span>
            </div>
          ))}
          {sortedRoles.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No user data available
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Quick action card component
interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

function QuickActionCard({
  title,
  description,
  icon,
  onClick,
  disabled,
}: QuickActionCardProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-left transition-all",
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md cursor-pointer",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <div className="w-5 h-5 text-gray-600 dark:text-gray-400">{icon}</div>
        </div>
        <div>
          <h3 className="font-medium text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
          {disabled && (
            <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
              Coming soon
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export function TenantDashboardTab() {
  const { data: stats, isLoading, error } = useTenantStats();

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
          Failed to load dashboard
        </h3>
        <p className="mt-2 text-red-600 dark:text-red-300">
          Unable to fetch organization statistics. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Organization Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold">
          {isLoading ? (
            <div className="h-8 w-48 bg-white/20 rounded animate-pulse" />
          ) : (
            stats?.tenantName || "Your Organization"
          )}
        </h2>
        <p className="mt-1 text-primary-100">Organization Dashboard</p>
      </div>

      {/* Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats?.users.totalUsers ?? 0}
            subtitle={`${stats?.users.activeUsers ?? 0} active`}
            icon={<UsersIcon />}
            loading={isLoading}
          />
          <StatCard
            title="Workshops"
            value={stats?.workshops.totalWorkshops ?? 0}
            subtitle={`${stats?.workshops.activeWorkshops ?? 0} active`}
            icon={<WorkshopIcon />}
            loading={isLoading}
          />
          <StatCard
            title="Exercises"
            value={stats?.workshops.totalExercises ?? 0}
            icon={<DocumentIcon />}
            loading={isLoading}
          />
          <StatCard
            title="Private Content"
            value={
              (stats?.privateContent.guides ?? 0) +
              (stats?.privateContent.libraryResources ?? 0) +
              (stats?.privateContent.agentTemplates ?? 0)
            }
            subtitle="Guides, resources, templates"
            icon={<BookIcon />}
            loading={isLoading}
          />
        </div>
      </div>

      {/* Users and Content Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Role Breakdown */}
        <RoleBreakdown byRole={stats?.users.byRole ?? {}} loading={isLoading} />

        {/* Private Content Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
            Private Content
          </h3>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 text-gray-500">
                    <DocumentIcon />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Guides
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {stats?.privateContent.guides ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 text-gray-500">
                    <BookIcon />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Library Resources
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {stats?.privateContent.libraryResources ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 text-gray-500">
                    <TemplateIcon />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Agent Templates
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {stats?.privateContent.agentTemplates ?? 0}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionCard
            title="Manage Users"
            description="Add or manage team members"
            icon={<UsersIcon />}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set("tab", "users");
              window.history.pushState(
                {},
                "",
                `${window.location.pathname}?${params}`,
              );
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          />
          <QuickActionCard
            title="Private Content"
            description="Manage organization content"
            icon={<BookIcon />}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set("tab", "my-content");
              window.history.pushState(
                {},
                "",
                `${window.location.pathname}?${params}`,
              );
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          />
          <QuickActionCard
            title="Settings"
            description="Configure organization"
            icon={<TemplateIcon />}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set("tab", "settings");
              window.history.pushState(
                {},
                "",
                `${window.location.pathname}?${params}`,
              );
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          />
          <QuickActionCard
            title="Help"
            description="Get assistance"
            icon={<DocumentIcon />}
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set("tab", "help");
              window.history.pushState(
                {},
                "",
                `${window.location.pathname}?${params}`,
              );
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          />
        </div>
      </div>
    </div>
  );
}
